import {
  ContractNature,
  FrameworkAgreementType,
  ProcedureType,
  ReservedParticipationType,
  ReservedExecutionType,
  SelectionCriterionType,
} from "../../../../../autogen/bff-api";

// ── Framework Agreement ──────────────────────────────────────────────

// t('Direct call-off') t('Ordering directly from the agreement without new competition')
// t('Mini-competition') t('New competition among the suppliers on the framework agreement')
// t('Both') t('Both direct call-off and mini-competition, depending on value')
export const FRAMEWORK_AGREEMENT_OPTIONS = [
  {
    value: FrameworkAgreementType.WithoutReopening,
    labelKey: "Direct call-off",
    descriptionKey: "Ordering directly from the agreement without new competition",
  },
  {
    value: FrameworkAgreementType.WithReopening,
    labelKey: "Mini-competition",
    descriptionKey: "New competition among the suppliers on the framework agreement",
  },
  {
    value: FrameworkAgreementType.Mixed,
    labelKey: "Both",
    descriptionKey: "Both direct call-off and mini-competition, depending on value",
  },
] as const;

export const FRAMEWORK_AGREEMENT_LABELS: Record<string, string> = Object.fromEntries(
  FRAMEWORK_AGREEMENT_OPTIONS.map((o) => [o.value, o.labelKey])
);

export const isFrameworkEnabled = (value: FrameworkAgreementType | null | undefined): boolean =>
  value != null && value !== FrameworkAgreementType.None;

// ── Contract Nature ──────────────────────────────────────────────────

// t('Goods') t('Purchase of goods or products')
// t('Services') t('Purchase of services')
// t('Construction') t('Building and construction work')
export const CONTRACT_NATURE_OPTIONS = [
  { value: ContractNature.Supplies, labelKey: "Goods", descriptionKey: "Purchase of goods or products" },
  { value: ContractNature.Services, labelKey: "Services", descriptionKey: "Purchase of services" },
  { value: ContractNature.Works, labelKey: "Construction", descriptionKey: "Building and construction work" },
] as const;

export const CONTRACT_NATURE_LABELS: Record<string, string> = Object.fromEntries(
  CONTRACT_NATURE_OPTIONS.map((o) => [o.value, o.labelKey])
);

// ── Procedure Type ───────────────────────────────────────────────────

// t('Open tender') t('All qualified suppliers can submit bids')
// t('Restricted tender') t('Pre-qualification before bid submission')
// t('Negotiated procedure') t('Negotiation with selected suppliers')
// t('Competitive dialogue') t('Dialogue phase before final bids')
export const PROCEDURE_TYPE_OPTIONS = [
  { value: ProcedureType.Open, labelKey: "Open tender", descriptionKey: "All qualified suppliers can submit bids", enabled: true },
  { value: ProcedureType.Restricted, labelKey: "Restricted tender", descriptionKey: "Pre-qualification before bid submission", enabled: false },
  { value: ProcedureType.NegotiatedWithPriorCall, labelKey: "Negotiated procedure", descriptionKey: "Negotiation with selected suppliers", enabled: false },
  { value: ProcedureType.CompetitiveDialogue, labelKey: "Competitive dialogue", descriptionKey: "Dialogue phase before final bids", enabled: false },
] as const;

export const PROCEDURE_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  PROCEDURE_TYPE_OPTIONS.map((o) => [o.value, o.labelKey])
);

// ── Reserved Participation ──────────────────────────────────────────

// t('None') t('No restriction on participation')
// t('Sheltered workshop') t('Reserved for sheltered workshops and similar enterprises')
// t('Public service mission') t('Reserved for organizations with a public service mission')
export const RESERVED_PARTICIPATION_OPTIONS = [
  { value: ReservedParticipationType.None, labelKey: "None", descriptionKey: "No restriction on participation" },
  { value: ReservedParticipationType.ShelteredWorkshop, labelKey: "Sheltered workshop", descriptionKey: "Reserved for sheltered workshops and similar enterprises" },
  { value: ReservedParticipationType.PublicServiceMission, labelKey: "Public service mission", descriptionKey: "Reserved for organizations with a public service mission" },
] as const;

export const RESERVED_PARTICIPATION_LABELS: Record<string, string> = Object.fromEntries(
  RESERVED_PARTICIPATION_OPTIONS.map((o) => [o.value, o.labelKey])
);

// ── Reserved Execution ──────────────────────────────────────────────

// t('Sheltered employment') t('Execution reserved for sheltered employment programmes')
// t('Not known') t('Reservation status not yet determined')
export const RESERVED_EXECUTION_OPTIONS = [
  { value: ReservedExecutionType.None, labelKey: "None", descriptionKey: "No restriction on execution" },
  { value: ReservedExecutionType.ShelteredEmployment, labelKey: "Sheltered employment", descriptionKey: "Execution reserved for sheltered employment programmes" },
  { value: ReservedExecutionType.NotKnown, labelKey: "Not known", descriptionKey: "Reservation status not yet determined" },
] as const;

export const RESERVED_EXECUTION_LABELS: Record<string, string> = Object.fromEntries(
  RESERVED_EXECUTION_OPTIONS.map((o) => [o.value, o.labelKey])
);

// ── Selection Criterion Type ────────────────────────────────────────
//
// "Selection criteria" (EU-direktiv 2014/24/EU) === "kvalifikasjonskrav" (FOA).
// The DTO field is `selectionCriteria`; the UI says "Kvalifikasjonskrav".
// See docs/adr/002-qualification-requirements.md (Decision 1).

// t('Suitability') t('Economic and financial') t('Technical and professional')
// t('Quality and environment') t('Other')
export const SELECTION_CRITERION_TYPE_OPTIONS = [
  { value: SelectionCriterionType.Suitability, labelKey: "Suitability", badgeBg: "smSecondarySoft", badgeColor: "smSecondary" },
  { value: SelectionCriterionType.EconomicFinancial, labelKey: "Economic and financial", badgeBg: "smQuaternarySoft", badgeColor: "smQuaternary" },
  { value: SelectionCriterionType.TechnicalProfessional, labelKey: "Technical and professional", badgeBg: "smPrimarySofter", badgeColor: "smPrimary" },
  { value: SelectionCriterionType.QualityEnvironment, labelKey: "Quality and environment", badgeBg: "smTertiarySoft", badgeColor: "smTertiary" },
  { value: SelectionCriterionType.Other, labelKey: "Other", badgeBg: "smBackgroundSecondary", badgeColor: "smMuted" },
] as const;
